/**
 * DraftBanner
 * --------------------------------------------------------------
 * Two pieces of UI for the auto-save draft system:
 *  - <DraftRestoreBanner>: amber alert at the top of a modal when a
 *    saved draft from a previous session is detected. Offers Restore /
 *    Discard actions.
 *  - <DraftSavedIndicator>: a tiny "Auto-saved Xs ago" footer chip that
 *    confirms autosave is working in real time.
 */
import { useEffect, useState } from 'react';
import { FileWarning, RotateCcw, Trash2, Check } from 'lucide-react';

function formatAgo(iso) {
  if (!iso) return '';
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 0) return 'just now';
  const sec = Math.floor(ms / 1000);
  if (sec < 5) return 'just now';
  if (sec < 60) return `${sec}s ago`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min} min ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr} hr ago`;
  const day = Math.floor(hr / 24);
  return `${day} day${day > 1 ? 's' : ''} ago`;
}

function useTicker(intervalMs = 5000) {
  const [, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), intervalMs);
    return () => clearInterval(id);
  }, [intervalMs]);
}

export function DraftRestoreBanner({ visible, savedAt, onRestore, onDiscard, label = 'document' }) {
  useTicker(10000);
  if (!visible) return null;
  return (
    <div
      data-testid="draft-restore-banner"
      className="mb-3 flex items-start gap-3 rounded-md border border-amber-300 bg-amber-50 px-3 py-2.5 text-sm dark:border-amber-700 dark:bg-amber-950/40"
    >
      <FileWarning size={18} className="mt-0.5 shrink-0 text-amber-600 dark:text-amber-400" />
      <div className="flex-1">
        <p className="font-medium text-amber-900 dark:text-amber-200">
          Unsaved draft found
        </p>
        <p className="text-xs text-amber-800 dark:text-amber-300">
          We saved your last {label} from {formatAgo(savedAt)}. Restore it to continue where you left off.
        </p>
      </div>
      <div className="flex gap-2">
        <button
          type="button"
          onClick={onRestore}
          data-testid="draft-restore-btn"
          className="inline-flex items-center gap-1 rounded-md bg-amber-600 px-2.5 py-1 text-xs font-medium text-white hover:bg-amber-700"
        >
          <RotateCcw size={12} /> Restore
        </button>
        <button
          type="button"
          onClick={onDiscard}
          data-testid="draft-discard-btn"
          className="inline-flex items-center gap-1 rounded-md border border-amber-300 bg-white px-2.5 py-1 text-xs font-medium text-amber-700 hover:bg-amber-100 dark:bg-transparent dark:text-amber-200 dark:hover:bg-amber-900/40"
        >
          <Trash2 size={12} /> Discard
        </button>
      </div>
    </div>
  );
}

export function DraftSavedIndicator({ savedAt }) {
  useTicker(5000);
  if (!savedAt) return null;
  return (
    <span
      data-testid="draft-saved-indicator"
      className="inline-flex items-center gap-1 text-xs text-emerald-600 dark:text-emerald-400"
      title={`Last auto-saved at ${new Date(savedAt).toLocaleTimeString()}`}
    >
      <Check size={12} /> Auto-saved {formatAgo(savedAt)}
    </span>
  );
}

export default DraftRestoreBanner;
